import classNames from 'classnames';
import type { ReactNode } from 'react';

import { Icon } from '../Icon';
import type { IconName } from '../Icon';

import styles from './FeaturedIcon.module.css';

export type FeaturedIconVariant = 'success' | 'warning' | 'error' | 'info' | 'gray';
export type FeaturedIconSize = 'sm' | 'md' | 'lg';

/** Glyph shown when the caller doesn't pass a custom `icon`. */
const DEFAULT_ICON: Record<FeaturedIconVariant, IconName> = {
  success: 'check-circle',
  warning: 'alert-triangle',
  error: 'alert-circle',
  info: 'info-circle',
  gray: 'alert-circle',
};

const ICON_SIZE: Record<FeaturedIconSize, number> = { sm: 20, md: 24, lg: 28 };

export interface FeaturedIconProps {
  variant?: FeaturedIconVariant;
  size?: FeaturedIconSize;
  /** Custom glyph (any node). Defaults to a per-variant icomoon glyph. */
  icon?: ReactNode;
  className?: string;
}

/**
 * Tinted, rounded icon container used at the top of a modal (and elsewhere). The soft fill,
 * ring and glyph colour all come from the per-colour `--{variant}-chip-*` semantic tokens, so
 * it themes light/dark for free. Decorative by default — the modal title names the dialog.
 *
 * @interim Seeded from the Figma "Featured icon"; replaceable by the UI Kit without API breakage.
 */
export function FeaturedIcon({
  variant = 'success',
  size = 'md',
  icon,
  className,
}: FeaturedIconProps) {
  return (
    <span className={classNames(styles.root, styles[variant], styles[`size-${size}`], className)}>
      {icon ?? <Icon name={DEFAULT_ICON[variant]} size={ICON_SIZE[size]} />}
    </span>
  );
}
